<script setup>
import { useLayout } from '@/layout/composables/layout';
import { useToast } from 'primevue/usetoast';
import AppConfigurator from './AppConfigurator.vue';
const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout();
import { AuthService } from '@/service/AuthService';
import router from '@/router';
import { ref, computed, onMounted, nextTick } from 'vue';
import { activeBranchId, getBranchName, activeAccountingPeriodId } from '@/core/js/common';
import { useConfirm } from 'primevue/useconfirm';
import { buttonSizeConstants, buttonCustomSizeConstants } from '@/core/js/constants';
import { validateField } from '@/core/directive/validation';
import { reactive } from 'vue';
import { UserService } from '@/service/definition/UserService';
import avatarImg from '@/assets/img/avatar3.png';

//#region form variables
const toast = useToast();
const confirm = useConfirm();
const searchText = ref('');
const user = AuthService.getUser();
const branch_name = ref('');
activeBranchId.value = AuthService.getActiveBranchId();
let previousBranch = AuthService.getActiveBranchId();
const branches = ref([]);
const showBranches = ref();
const accounting_period_name = ref('');
const accounting_period_status = ref('');
activeAccountingPeriodId.value = AuthService.getActiveAccountingPeriodId();
let previousAccountingPeriodId = AuthService.getActiveAccountingPeriodId();
const accountingPeriods = ref([]);
const showAccountingPeriods = ref();
//#endregion

//#region update password
const updatePasswordDialog = ref(false);
const initialForm = {
    user_id: AuthService.getUser().id || null,
    current_password: '',
    password: ''
};
const form = reactive({ ...initialForm });
const onUpdatePassword = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    const fields = document.querySelectorAll('[data-validate="true"]');
    fields.forEach((el) => {
        const binding = { value: { rules: el.__rules || [] } };
        validateField(el, binding);
    });
    const allValid = Array.from(document.querySelectorAll('.validation-error')).every((el) => !el.textContent);

    if (!allValid) {
        return;
    }
    confirm.require({
        header: 'Confirmation',
        message: 'Are you sure you want to update?',
        icon: 'pi pi-exclamation-circle',
        rejectProps: {
            label: 'No',
            icon: 'pi pi-times',
            severity: 'danger'
        },
        acceptProps: {
            label: 'Yes',
            icon: 'pi pi-check'
        },
        accept: async () => {
            const response = await UserService.updatePassword(form);
            if (!response) return false;
            updatePasswordDialog.value = false;
            Object.assign(form, { ...initialForm });
        },
        reject: () => {}
    });
};
//#endregion

//#region logout
function Logout() {
    AuthService.logout();
    nextTick();
    // router.replace('/login');
    // router.push('/login');
    window.location.href = '/login';
    toast.add({
        severity: 'success',
        summary: 'Logged Out',
        detail: 'You have been signed out successfully.',
        life: 3000
    });
}
//#endregion

//#region getSet function
onMounted(async () => {
    const getBranches = localStorage.getItem('userBranches');
    branches.value = getBranches ? JSON.parse(getBranches) : [];
    branch_name.value = getBranchName(branches.value, activeBranchId.value);
    getActiveAccountingPeriodDetail();
});
function getActiveAccountingPeriodDetail() {
    const getAccountingPeriods = localStorage.getItem('userAccountingPeriods');
    accountingPeriods.value = getAccountingPeriods ? JSON.parse(getAccountingPeriods) : [];
    const get_active_accounting_period_detail = accountingPeriods.value.find((accountingPeriod) => accountingPeriod.accounting_period_id === activeAccountingPeriodId.value);
    if (get_active_accounting_period_detail) {
        accounting_period_name.value = get_active_accounting_period_detail.name;
        accounting_period_status.value = get_active_accounting_period_detail.period_status;
    }
}
const onBranchChange = (id) => {
    confirm.require({
        header: 'Confirmation',
        message: 'Are you sure you want to switch branch?',
        icon: 'pi pi-question-circle',
        acceptClass: 'p-button',
        rejectClass: 'p-button-danger',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
            previousBranch = id;
            activeBranchId.value = id;
            localStorage.setItem('activeBranchId', JSON.stringify(id));
            branch_name.value = getBranchName(branches.value, id);
            router.push('/');
            toast.add({
                severity: 'success',
                summary: 'Branch Switched',
                detail: 'You have successfully switched to the selected branch.',
                life: 3000
            });
        },
        reject: () => {
            activeBranchId.value = previousBranch;
            localStorage.setItem('activeBranchId', JSON.stringify(previousBranch));
            branch_name.value = getBranchName(branches.value, previousBranch);
        }
    });
};
function toggleBranches(event) {
    showBranches.value.toggle(event);
}
const onAccountingPeriodChange = (id) => {
    confirm.require({
        header: 'Confirmation',
        message: 'Are you sure you want to switch accounting period?',
        icon: 'pi pi-question-circle',
        acceptClass: 'p-button',
        rejectClass: 'p-button-danger',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
            previousAccountingPeriodId = id;
            activeAccountingPeriodId.value = id;
            localStorage.setItem('activeAccountingPeriodId', JSON.stringify(id));
            getActiveAccountingPeriodDetail();
            router.push('/');
            toast.add({
                severity: 'success',
                summary: 'Accounting Period Switched',
                detail: 'You have successfully switched to the selected accounting period.',
                life: 3000
            });
        },
        reject: () => {
            activeAccountingPeriodId.value = previousAccountingPeriodId;
            localStorage.setItem('activeAccountingPeriodId', JSON.stringify(previousAccountingPeriodId));
            getActiveAccountingPeriodDetail();
        }
    });
};

function toggleAccountingPeriods(event) {
    showAccountingPeriods.value.toggle(event);
}
const filteredMenu = computed(() => {
    const flatMenu = [];
    const menuJson = localStorage.getItem('menu');
    const parsedMenu = menuJson ? JSON.parse(menuJson) : {};
    const branchId = String(activeBranchId.value);
    const fullMenu = parsedMenu[branchId] || [];
    function flatten(menuItems, lvl1Name = '') {
        menuItems.forEach((item) => {
            const currentLvl1Name = item.lvl === '1' ? item.name : lvl1Name;

            if (item.link && item.link !== '') {
                flatMenu.push({
                    ...item,
                    lvl1Name: currentLvl1Name
                });
            }

            if (item.children && item.children.length > 0) {
                flatten(item.children, currentLvl1Name);
            }
        });
    }
    flatten(fullMenu);
    if (!searchText.value) return [];
    return flatMenu.filter((item) => item.name.toLowerCase().includes(searchText.value.toLowerCase())).slice(0, 4);
});
function goToRoute(link) {
    if (link) {
        router.push(link);
        searchText.value = '';
    }
}
const dropdownOptions = ref();
const userDropdownOptions = [
    {
        label: 'Change Password',
        icon: 'pi pi-key',
        command: () => {
            updatePasswordDialog.value = true;
        }
    },
    {
        label: 'Logout',
        icon: 'pi pi-power-off',
        command: () => {
            Logout();
        }
    }
];
//#endregion
</script>
<template>
    <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            <router-link to="/" class="layout-topbar-logo">
                <img src="/src/assets/img/solexp_logo2.png" alt="Logo" style="width: 40px" />
                <span class="hidden lg:inline">Solution Experts</span>
            </router-link>
            <button class="layout-menu-button layout-topbar-action ml-5" @click="toggleMenu">
                <i class="pi pi-bars"></i>
            </button>
        </div>
        <div class="layout-topbar-actions relative flex items-center space-x-2 h-14">
            <div class="hidden lg:inline relative">
                <IconField>
                    <InputText v-model="searchText" placeholder="Quick Menu Search" class="md:w-[20rem] pr-10" :size="buttonSizeConstants.size" />
                    <InputIcon class="pi pi-search" />
                </IconField>
                <div v-if="filteredMenu.length" class="absolute z-50 w-full bg-white mt-1 rounded shadow border border-gray-200 max-h-64 overflow-y-auto">
                    <ul class="list-none m-0 p-0">
                        <li v-for="item in filteredMenu" :key="item.id" class="p-2 hover:bg-gray-100 cursor-pointer" @click="goToRoute(item.link)">
                            <strong>{{ item.lvl1Name }}</strong> / {{ item.name }}
                        </li>
                    </ul>
                </div>
                <div v-else-if="searchText" class="absolute z-50 w-full bg-white mt-1 p-2 text-gray-400 rounded shadow border border-gray-200">No results found</div>
            </div>
            <!-- Config menu (dark mode, palette, user info) -->
            <div class="flex items-center justify-end h-full space-x-3">
                <!-- Dark Mode Toggle -->
                <Button :icon="isDarkTheme ? 'pi pi-moon' : 'pi pi-sun'" @click="toggleDarkMode" severity="secondary" rounded :size="buttonSizeConstants.size" />
                <!-- Palette Config Button -->
                <Button
                    icon="pi pi-palette"
                    :size="buttonSizeConstants.size"
                    rounded
                    severity="secondary"
                    v-styleclass="{
                        selector: '@next',
                        enterFromClass: 'hidden',
                        enterActiveClass: 'animate-scalein',
                        leaveToClass: 'hidden',
                        leaveActiveClass: 'animate-fadeout',
                        hideOnOutsideClick: true
                    }"
                />

                <AppConfigurator />
                <!-- Accounting Periods toggle button -->
                <Button icon="pi pi-calendar" @click="toggleAccountingPeriods($event)" severity="secondary" rounded :size="buttonSizeConstants.size"> </Button>
                <!-- Accounting Periods dropdown panel -->
                <OverlayPanel ref="showAccountingPeriods" appendTo="body">
                    <ul class="branch-dropdown max-h-64 overflow-y-auto">
                        <li
                            v-for="a in accountingPeriods"
                            :key="a.accounting_period_id"
                            @click="onAccountingPeriodChange(a.accounting_period_id)"
                            :class="{ selected: activeAccountingPeriodId === a.accounting_period_id }"
                            class="cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                        >
                            <span
                                >{{ a.name }} <span class="text-xs text-gray-500">({{ a.period_status }})</span></span
                            >
                        </li>
                    </ul>
                </OverlayPanel>

                <!-- Branch toggle button -->
                <Button icon="pi pi-map-marker" @click="toggleBranches($event)" severity="secondary" rounded :size="buttonSizeConstants.size"> </Button>
                <!-- Branch dropdown panel -->
                <OverlayPanel ref="showBranches" appendTo="body">
                    <ul class="branch-dropdown max-h-64 overflow-y-auto">
                        <li v-for="b in branches" :key="b.id" @click="onBranchChange(b.id)" :class="{ selected: activeBranchId === b.id }" class="cursor-pointer px-4 py-2 hover:bg-gray-100">
                            {{ b.name }}
                        </li>
                    </ul>
                </OverlayPanel>
                <!-- User Info with Avatar -->
                <div class="flex items-center space-x-3 sm:w-auto h-full">
                    <!-- Avatar -->
                    <div class="flex flex-col justify-center">
                        <Avatar
                            :image="avatarImg"
                            class="rounded-full"
                            :style="{
                                width: buttonCustomSizeConstants.size,
                                height: buttonCustomSizeConstants.size
                            }"
                        />
                    </div>
                    <!-- User Info -->
                    <div class="flex flex-col justify-center text-gray-800">
                        <!-- Username -->
                        <span class="text-sm font-medium text-gray-700"> {{ user.username }}</span>

                        <!-- Branch + Accounting Info with Dropdown -->
                        <span class="cursor-pointer flex flex-row items-center gap-2 text-sm text-gray-600" @click="dropdownOptions.toggle($event)">
                            <i class="pi pi-map-marker text-xs text-gray-400"></i>
                            <span>{{ branch_name }}</span>
                            <i class="pi pi-calendar text-xs text-gray-400"></i>
                            <span
                                >{{ accounting_period_name }} <span class="text-xs text-gray-500">({{ accounting_period_status }})</span>
                            </span>
                            <i class="pi pi-chevron-down text-xs ml-1"></i>
                        </span>

                        <!-- PrimeVue Dropdown Menu -->
                        <Menu ref="dropdownOptions" :model="userDropdownOptions" popup />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Dialog v-model:visible="updatePasswordDialog" position="top" modal header="Update Password" :style="{ width: '25rem' }">
        <form @submit="onUpdatePassword">
            <div class="grid grid-cols-1 gap-8">
                <div class="flex flex-col gap-2">
                    <label> Current Password</label>
                    <Password v-model="form.current_password" toggleMask :feedback="false" placeholder="Current Password" fluid v-validate="{ rules: ['required'] }" />
                </div>
                <div class="flex flex-col gap-2">
                    <label> New Password</label>
                    <Password v-model="form.password" toggleMask placeholder="New Password" fluid v-validate="{ rules: ['required'] }" />
                </div>
                <Button type="submit" label="Update Password" @click="visible = false" class="w-full"></Button>
            </div>
        </form>
    </Dialog>
</template>
